import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import './DocumentReview.css';

const DocumentReview = () => {
  const { revisionId } = useParams();
  const [revision, setRevision] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Inline commenting state
  const [selectedText, setSelectedText] = useState('');
  const [selectionPosition, setSelectionPosition] = useState(null);
  const [showInlineCommentBox, setShowInlineCommentBox] = useState(false);
  const [inlineCommentText, setInlineCommentText] = useState('');
  const [commentType, setCommentType] = useState('suggestion');
  const [overallReviewStatus, setOverallReviewStatus] = useState('pending');
  const [reviewNotes, setReviewNotes] = useState('');
  const [hoveredCommentId, setHoveredCommentId] = useState(null);
  const [activeCommentId, setActiveCommentId] = useState(null);
  
  const contentRef = useRef(null);

  useEffect(() => {
    loadRevision();
    loadComments();
  }, [revisionId]);

  const loadRevision = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.PUBLIC_VIEW(revisionId));
      if (response.data.status === 'success') {
        setRevision(response.data.revision);
      } else {
        setError('Failed to load document revision');
      }
    } catch (err) {
      console.error('Error loading revision:', err);
      setError('Document revision not found');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.COMMENTS.GET(revisionId));
      if (response.data.status === 'success') {
        setComments(response.data.comments || []);
      }
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  };

  // Enhanced function to highlight text with comments
  const highlightTextWithComments = (text) => {
    if (!comments.length) return text;
    
    const inlineComments = comments.filter(c => c.is_inline && c.selected_text);
    if (!inlineComments.length) return text;
    
    let highlightedText = text;
    
    // Sort comments by text length (longest first) to avoid nested highlighting issues
    const sortedComments = [...inlineComments].sort((a, b) => b.selected_text.length - a.selected_text.length);
    
    sortedComments.forEach(comment => {
      const selectedText = comment.selected_text;
      const commentId = comment.id;
      const commentType = comment.comment_type;
      
      // Create a unique marker for this comment
      const marker = `<span class="highlighted-text ${commentType}" 
        data-comment-id="${commentId}" 
        onMouseEnter={() => setHoveredCommentId('${commentId}')}
        onMouseLeave={() => setHoveredCommentId(null)}
        onClick={() => setActiveCommentId('${commentId}')}
        title="${comment.text}">${selectedText}</span>`;
      
      // Replace the text with highlighted version
      highlightedText = highlightedText.replace(selectedText, marker);
    });
    
    return highlightedText;
  };

  // Render document content with highlighted comments
  const renderDocumentContent = () => {
    if (!revision?.content) return null;
    
    const inlineComments = comments.filter(c => c.is_inline && c.selected_text);
    
    // Apply highlighting to the full HTML content instead of splitting it
    let processedContent = revision.content;
    
    inlineComments.forEach(comment => {
      const selectedText = comment.selected_text;
      const commentId = comment.id;
      const commentType = comment.comment_type;
      const isActive = activeCommentId === commentId;
      const isHovered = hoveredCommentId === commentId;
      const isRejected = comment.rejected;
      
      const highlightClass = `highlighted-text ${commentType} ${isActive ? 'active' : ''} ${isHovered ? 'hovered' : ''} ${isRejected ? 'rejected' : ''}`;
      
      const highlightedSpan = `<span class="${highlightClass}" 
        data-comment-id="${commentId}" 
        title="${comment.text.substring(0, 100)}...">${selectedText}</span>`;
      
      // Only replace the first occurrence to avoid duplicates
      processedContent = processedContent.replace(selectedText, highlightedSpan);
    });
    
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: processedContent }}
        onClick={handleParagraphClick}
      />
    );
  };

  const handleParagraphClick = (e) => {
    const target = e.target;
    if (target.classList.contains('highlighted-text')) {
      const commentId = target.getAttribute('data-comment-id');
      setActiveCommentId(commentId);
      
      // Scroll to the comment in the sidebar
      const commentElement = document.querySelector(`[data-comment-id="${commentId}"]`);
      if (commentElement) {
        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && selection.toString().trim().length > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = contentRef.current.getBoundingClientRect();
      
      setSelectedText(selection.toString());
      setSelectionPosition({
        top: rect.bottom - containerRect.top + 10,
        left: rect.left - containerRect.left,
        width: rect.width
      });
      setShowInlineCommentBox(true);
    }
  };

  const handleAddInlineComment = async () => {
    if (!inlineCommentText.trim() || !selectedText) return;

    try {
      setSubmittingComment(true);
      const response = await axios.post(
        API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.COMMENTS.ADD(revisionId),
        {
          text: inlineCommentText,
          author: revision?.reviewer_name || 'Reviewer',
          comment_type: commentType,
          selected_text: selectedText,
          is_inline: true
        }
      );

      if (response.data.status === 'success') {
        setInlineCommentText('');
        setSelectedText('');
        setShowInlineCommentBox(false);
        setSelectionPosition(null);
        loadComments();
        
        // Clear text selection
        window.getSelection().removeAllRanges();
      }
    } catch (err) {
      console.error('Error adding inline comment:', err);
      alert('Failed to add inline comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAddGeneralComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmittingComment(true);
      const response = await axios.post(
        API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.COMMENTS.ADD(revisionId),
        {
          text: newComment,
          author: revision?.reviewer_name || 'Reviewer',
          comment_type: 'general',
          is_inline: false
        }
      );

      if (response.data.status === 'success') {
        setNewComment('');
        loadComments();
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleResolveComment = async (commentId) => {
    try {
      await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.COMMENTS.RESOLVE(revisionId, commentId));
      loadComments();
    } catch (err) {
      console.error('Error resolving comment:', err);
      alert('Failed to resolve comment');
    }
  };

  const handleSubmitReview = async () => {
    try {
      const response = await axios.put(
        API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.UPDATE(revisionId),
        {
          status: overallReviewStatus,
          review_notes: reviewNotes,
          reviewed_by: revision?.reviewer_name || 'Reviewer',
          reviewed_at: new Date().toISOString()
        }
      );

      if (response.data.status === 'success') {
        alert(`Review submitted successfully! Status: ${overallReviewStatus}`);
        loadRevision();
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      alert('Failed to submit review');
    }
  };

  const getCommentTypeIcon = (type) => {
    switch (type) {
      case 'approval': return '✅';
      case 'rejection': return '❌';
      case 'suggestion': return '💡';
      case 'question': return '❓';
      case 'general': return '💬';
      default: return '💬';
    }
  };

  const getCommentTypeColor = (type) => {
    switch (type) {
      case 'approval': return '#28a745';
      case 'rejection': return '#dc3545';
      case 'suggestion': return '#ffc107';
      case 'question': return '#17a2b8';
      case 'general': return '#6c757d';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="document-review-container">
        <div className="loading">Loading document revision...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-review-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="document-review-container">
      <div className="document-review-header">
        <h1>Document Review</h1>
        <div className="revision-info">
          <h2>{revision?.title}</h2>
          <p><strong>Reviewer:</strong> {revision?.reviewer_name}</p>
          {revision?.due_date && (
            <p><strong>Due Date:</strong> {new Date(revision.due_date).toLocaleDateString()}</p>
          )}
          <p><strong>Status:</strong> <span className={`status ${revision?.status}`}>{revision?.status}</span></p>
          
          {/* Timeline Section */}
          <div className="revision-timeline">
            <h4>📅 Timeline</h4>
            {revision?.created_at && (
              <div className="timeline-item">
                <span className="timeline-icon">📝</span>
                <span className="timeline-label">Sent for review:</span>
                <span className="timeline-value">{new Date(revision.created_at).toLocaleDateString()} at {new Date(revision.created_at).toLocaleTimeString()}</span>
              </div>
            )}
            {comments && comments.length > 0 && (
              <div className="timeline-item">
                <span className="timeline-icon">💬</span>
                <span className="timeline-label">Total comments:</span>
                <span className="timeline-value">{comments.length}</span>
              </div>
            )}
            {comments && comments.length > 0 && (
              <div className="timeline-item">
                <span className="timeline-icon">🕒</span>
                <span className="timeline-label">Latest comment:</span>
                <span className="timeline-value">
                  {new Date(comments[comments.length - 1].created_at).toLocaleDateString()} at {new Date(comments[comments.length - 1].created_at).toLocaleTimeString()}
                </span>
              </div>
            )}
            {revision?.reviewed_at && (
              <div className="timeline-item">
                <span className="timeline-icon">✅</span>
                <span className="timeline-label">Reviewed:</span>
                <span className="timeline-value">
                  {new Date(revision.reviewed_at).toLocaleDateString()} at {new Date(revision.reviewed_at).toLocaleTimeString()}
                  {revision.reviewed_by && ` by ${revision.reviewed_by}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="review-instructions">
        <h3>📋 Review Instructions</h3>
        <ul>
          <li><strong>Select text</strong> to add inline comments with specific feedback</li>
          <li><strong>Click highlighted text</strong> to view existing comments and suggestions</li>
          <li><strong>Choose comment type:</strong> 💡 Suggestion, ❓ Question, ✅ Approval, or ❌ Rejection</li>
          <li><strong>Add general comments</strong> for overall feedback</li>
          <li><strong>Submit your final review</strong> with approval or request for changes</li>
        </ul>
      </div>

      <div className="review-layout">
        <div className="document-content" ref={contentRef}>
          <h3>Document Content</h3>
          <div 
            className="content-text selectable-content ql-editor"
            onMouseUp={handleTextSelection}
            style={{ position: 'relative' }}
          >
            {renderDocumentContent()}
            
            {/* Inline comment box */}
            {showInlineCommentBox && selectionPosition && (
              <div 
                className="inline-comment-box"
                style={{
                  position: 'absolute',
                  top: selectionPosition.top,
                  left: selectionPosition.left,
                  zIndex: 1000
                }}
              >
                <div className="selected-text-preview">
                  <strong>Selected:</strong> "{selectedText}"
                </div>
                <div className="comment-type-selector">
                  <label>Comment Type:</label>
                  <select 
                    value={commentType} 
                    onChange={(e) => setCommentType(e.target.value)}
                  >
                    <option value="suggestion">💡 Suggestion</option>
                    <option value="question">❓ Question</option>
                    <option value="approval">✅ Approval</option>
                    <option value="rejection">❌ Rejection</option>
                  </select>
                </div>
                <textarea
                  value={inlineCommentText}
                  onChange={(e) => setInlineCommentText(e.target.value)}
                  placeholder="Add your comment about the selected text..."
                  rows="3"
                  autoFocus
                />
                <div className="inline-comment-actions">
                  <button
                    onClick={handleAddInlineComment}
                    disabled={submittingComment || !inlineCommentText.trim()}
                    className="submit-inline-comment-btn"
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </button>
                  <button
                    onClick={() => {
                      setShowInlineCommentBox(false);
                      setSelectedText('');
                      setInlineCommentText('');
                      setSelectionPosition(null);
                      window.getSelection().removeAllRanges();
                    }}
                    className="cancel-inline-comment-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="comments-sidebar">
          <h3>Comments & Feedback</h3>
          
          {/* Active Comment Display */}
          {activeCommentId && (
            <div className="active-comment-display">
              {(() => {
                const activeComment = comments.find(c => c.id === activeCommentId);
                if (!activeComment) return null;
                
                return (
                  <div className={`comment active-comment ${activeComment.comment_type}`}>
                    <div className="comment-header">
                      <span 
                        className="comment-type-badge"
                        style={{ backgroundColor: getCommentTypeColor(activeComment.comment_type) }}
                      >
                        {getCommentTypeIcon(activeComment.comment_type)} {activeComment.comment_type}
                      </span>
                      <button
                        onClick={() => setActiveCommentId(null)}
                        className="close-comment-btn"
                      >
                        ×
                      </button>
                    </div>
                    <div className="comment-author">
                      <strong>{activeComment.author}</strong>
                      <span className="comment-date">
                        {new Date(activeComment.created_at).toLocaleString()}
                      </span>
                    </div>
                    {activeComment.selected_text && (
                      <div className="selected-text-quote">
                        <strong>Reference:</strong> "{activeComment.selected_text}"
                      </div>
                    )}
                    <div className="comment-text">{activeComment.text}</div>
                    {!activeComment.resolved && (
                      <button
                        onClick={() => handleResolveComment(activeComment.id)}
                        className="resolve-btn"
                      >
                        Mark as Resolved
                      </button>
                    )}
                    {activeComment.resolved && (
                      <div className="resolved-indicator">✓ Resolved</div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          
          {/* Inline Comments */}
          <div className="inline-comments">
            <h4>All Inline Comments ({comments.filter(c => c.is_inline).length})</h4>
            {comments.filter(c => c.is_inline).map((comment) => (
              <div 
                key={comment.id} 
                className={`comment inline-comment ${comment.resolved ? 'resolved' : ''} ${comment.rejected ? 'rejected' : ''} ${activeCommentId === comment.id ? 'selected' : ''}`}
                data-comment-id={comment.id}
                onClick={() => setActiveCommentId(comment.id)}
                onMouseEnter={() => setHoveredCommentId(comment.id)}
                onMouseLeave={() => setHoveredCommentId(null)}
              >
                <div className="comment-header">
                  <span 
                    className="comment-type-badge"
                    style={{ backgroundColor: getCommentTypeColor(comment.comment_type) }}
                  >
                    {getCommentTypeIcon(comment.comment_type)} {comment.comment_type}
                  </span>
                  <strong>{comment.author}</strong>
                  <span className="comment-date">
                    {new Date(comment.created_at).toLocaleString()}
                  </span>
                </div>
                {comment.selected_text && (
                  <div className="selected-text-quote">
                    <strong>Reference:</strong> "{comment.selected_text.substring(0, 50)}..."
                  </div>
                )}
                <div className="comment-text">{comment.text.substring(0, 100)}...</div>
                {comment.resolved && (
                  <div className="resolved-indicator">✓ Resolved</div>
                )}
                {comment.rejected && (
                  <div className="rejected-indicator">× Rejected</div>
                )}
              </div>
            ))}
          </div>

          {/* General Comments */}
          <div className="general-comments">
            <h4>General Comments</h4>
            <div className="add-comment">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add general feedback about the document..."
                rows="4"
              />
              <button
                onClick={handleAddGeneralComment}
                disabled={submittingComment || !newComment.trim()}
                className="submit-comment-btn"
              >
                {submittingComment ? 'Adding...' : 'Add General Comment'}
              </button>
            </div>

            <div className="comments-list">
              {comments.filter(c => !c.is_inline).length === 0 ? (
                <p className="no-comments">No general comments yet.</p>
              ) : (
                comments.filter(c => !c.is_inline).map((comment) => (
                  <div key={comment.id} className={`comment ${comment.resolved ? 'resolved' : ''} ${comment.rejected ? 'rejected' : ''}`}>
                    <div className="comment-header">
                      <strong>{comment.author}</strong>
                      <span className="comment-date">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                      {!comment.resolved && (
                        <button
                          onClick={() => handleResolveComment(comment.id)}
                          className="resolve-btn"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                    <div className="comment-text">{comment.text}</div>
                    {comment.resolved && (
                      <div className="resolved-indicator">✓ Resolved</div>
                    )}
                    {comment.rejected && (
                      <div className="rejected-indicator">× Rejected</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Final Review Section */}
      <div className="final-review-section">
        <h3>📝 Final Review</h3>
        <div className="review-status-selector">
          <label>Overall Review Decision:</label>
          <div className="status-options">
            <label className="status-option">
              <input
                type="radio"
                value="approved"
                checked={overallReviewStatus === 'approved'}
                onChange={(e) => setOverallReviewStatus(e.target.value)}
              />
              <span className="status-label approved">✅ Approved</span>
            </label>
            <label className="status-option">
              <input
                type="radio"
                value="changes_requested"
                checked={overallReviewStatus === 'changes_requested'}
                onChange={(e) => setOverallReviewStatus(e.target.value)}
              />
              <span className="status-label changes-requested">🔄 Changes Requested</span>
            </label>
            <label className="status-option">
              <input
                type="radio"
                value="rejected"
                checked={overallReviewStatus === 'rejected'}
                onChange={(e) => setOverallReviewStatus(e.target.value)}
              />
              <span className="status-label rejected">❌ Rejected</span>
            </label>
          </div>
        </div>

        <div className="review-notes">
          <label>Summary Notes:</label>
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Provide a summary of your review and any additional notes..."
            rows="4"
          />
        </div>

        <button
          onClick={handleSubmitReview}
          className="submit-review-btn"
          disabled={!overallReviewStatus || overallReviewStatus === 'pending'}
        >
          Submit Final Review
        </button>
      </div>
    </div>
  );
};

export default DocumentReview; 